import React, { useState } from "react";
import { Check, Loader2 } from "lucide-react";

const val = (x, c) => (typeof x === "function" ? x(c) : x);
const input = "w-full bg-black/40 border border-white/10 focus:border-nex-gold/60 outline-none text-nex-silver text-sm px-2.5 py-1.5 rounded-sm";
export const btn = "inline-flex items-center gap-1.5 px-3 py-1.5 border font-mono text-[10px] tracking-[0.16em] uppercase rounded-sm disabled:opacity-40";

function Field({ f, c, v, set, id }) {
  const common = { value: v ?? "", onChange: (e) => set(e.target.value), "data-testid": id, className: input };
  if (f.type === "textarea") return <textarea rows={4} {...common} />;
  if (f.type === "select") {
    const opts = f.options(c);
    return <select {...common}><option value="">Pick one…</option>{opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>;
  }
  if (f.type === "pin") return <input {...common} inputMode="numeric" maxLength={6} onChange={(e) => set(e.target.value.replace(/\D/g, ""))} />;
  return <input type={f.type || "text"} {...common} />;
}

/** One action in a guided step: fill fields → "Confirm this change?" → run. Nothing saves until Confirm. */
export function FixAction({ a, c, api, done, onDone, testid }) {
  const [v, setV] = useState(() => Object.fromEntries((a.fields || []).map((f) => [f.name, val(f.default, c) ?? ""])));
  const [stage, setStage] = useState("edit");
  const [err, setErr] = useState("");
  const missing = (a.fields || []).filter((f) => !String(v[f.name] ?? "").trim() && !val(f.optional, c)).map((f) => f.label);
  const short = (a.fields || []).find((f) => (f.min && String(v[f.name] || "").trim().length < f.min) || (f.type === "pin" && String(v[f.name] || "").length !== 6));
  if (done) return <p data-testid={`${testid}-done`} className="flex items-center gap-2 text-sm text-emerald-300"><Check className="h-4 w-4" /> {a.done}</p>;
  const run = async () => {
    setStage("busy"); setErr("");
    try { await a.run(v, c, api); onDone(); } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join("; ") : "That didn't work. Try again.");
      setStage("edit");
    }
  };
  const red = a.tone === "red";
  return (
    <div data-testid={testid} className={`p-3 rounded-sm border space-y-2 ${red ? "border-red-400/30 bg-red-500/[0.04]" : "border-white/10 bg-white/[0.02]"}`}>
      {stage === "edit" && <>
        {(a.fields || []).map((f) => <label key={f.name} className="block space-y-1"><span className="text-xs text-nex-muted">{f.label}{val(f.optional, c) ? " (optional)" : ""}</span>
          <Field f={f} c={c} v={v[f.name]} set={(x) => setV((o) => ({ ...o, [f.name]: x }))} id={`${testid}-field-${f.name}`} />
          {f.min && <span className="text-[11px] text-nex-muted">{String(v[f.name] || "").trim().length} / {f.min}</span>}</label>)}
        {err && <p data-testid={`${testid}-error`} className="text-sm text-red-300">{err}</p>}
        <button type="button" disabled={missing.length > 0 || !!short} onClick={() => setStage("confirm")} data-testid={`${testid}-start`}
          className={`${btn} ${red ? "border-red-400/60 text-red-300 hover:bg-red-500/10" : "border-nex-gold text-nex-gold hover:bg-nex-gold/10"}`}>{a.label}</button>
        {missing.length > 0 && <span className="ml-2 text-[11px] text-nex-muted">Fill in: {missing.join(", ")}</span>}
      </>}
      {stage !== "edit" && <div data-testid={`${testid}-confirm`} className="space-y-2">
        <p className="text-sm text-amber-100"><b>Confirm this change?</b> {a.confirm(v, c)}</p>
        <div className="flex gap-2">
          <button type="button" disabled={stage === "busy"} onClick={run} data-testid={`${testid}-confirm-yes`} className={`${btn} border-emerald-400/60 text-emerald-300 hover:bg-emerald-500/10`}>
            {stage === "busy" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Confirm</button>
          <button type="button" disabled={stage === "busy"} onClick={() => setStage("edit")} data-testid={`${testid}-confirm-no`} className={`${btn} border-white/15 text-nex-muted hover:text-nex-silver`}>Go back</button>
        </div>
      </div>}
    </div>
  );
}
